/**
 * QR codes, as modules rather than as a picture.
 *
 * The encoder gives a grid of dark and light squares, and that is exactly what
 * we want: drawn as rectangles, a QR code is vector at every size, so it prints
 * at the press's own resolution instead of at whatever a rasteriser guessed.
 * A PNG on a business card is the classic reason a code will not scan.
 *
 * Encoding is memoised because a customer typing a URL re-encodes on every
 * keystroke, and Reed-Solomon is not free.
 */

import qrcode from 'qrcode-generator';

/*
 * UTF-8, not the library's default.
 *
 * Out of the box qrcode-generator keeps only the low byte of each character,
 * which is right for Latin-1 and wrong for everything else: an Arabic name in a
 * contact card, or the typographic apostrophe in "Prismat’s", came out of a
 * phone as garbage. Phones read byte-mode codes as UTF-8, so that is what goes
 * in. A code holding only ASCII - which is nearly every code ever made here -
 * encodes to exactly the same bytes as before.
 */
const utf8 = new TextEncoder();

qrcode.stringToBytes = (text: string) => Array.from(utf8.encode(text));
import type { QrLayer } from '../model/types';
import { buildPayload, webAddress } from './qr-types';

export interface QrGrid {
  /** Modules per side, including the code's own quiet zone allowance. */
  count: number;
  /** Row-major, true where the module is dark. */
  dark: boolean[];
}

/**
 * The quiet zone, in modules.
 *
 * Four is what the specification requires, and scanners genuinely fail without
 * it. It is part of the code rather than a margin the customer can crop off, so
 * it is built into the grid here.
 */
export const QUIET = 4;

/** Below this, printed modules blur into each other and the code stops scanning. */
export const MIN_MODULE_MM = 0.4;

const cache = new Map<string, QrGrid | null>();
const LIMIT = 32;

/**
 * The payload a scanner should see.
 *
 * A code with fields is built from them (codes/qr-types.ts). A code saved
 * before fields existed is read from its one string, exactly as it was then:
 * an approved design must go on printing the code that was approved.
 */
export function payload(layer: QrLayer): string {
  if (layer.fields && Object.keys(layer.fields).length > 0) {
    return buildPayload(layer.kind, layer.fields);
  }

  return legacyPayload(layer);
}

/** The one-string codes, as they have always been read. */
function legacyPayload(layer: QrLayer): string {
  const value = layer.value.trim();

  if (value === '') return '';

  switch (layer.kind) {
    case 'url':
      return webAddress(value);

    case 'wifi':
      // SSID, or "SSID:password" - the shortest thing a shop is likely to type.
      {
        const [ssid, password] = value.split(':');

        return `WIFI:T:${password ? 'WPA' : 'nopass'};S:${escapeWifi(ssid ?? '')};${
          password ? `P:${escapeWifi(password)};` : ''
        };`;
      }

    case 'vcard':
      // Name, then whatever else was given, one per line.
      {
        const [name, ...rest] = value.split('\n');

        return [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `FN:${name ?? ''}`,
          ...rest.filter(Boolean).map((line) => (line.includes('@') ? `EMAIL:${line}` : `TEL:${line}`)),
          'END:VCARD',
        ].join('\n');
      }

    default:
      return value;
  }
}

function escapeWifi(text: string): string {
  return text.replace(/([\\;,:"])/g, '\\$1');
}

/**
 * The module grid, or null when there is nothing to encode.
 *
 * Null is also what an over-long payload gives: the encoder throws when the
 * data will not fit even the largest version, and a customer pasting an essay
 * into a QR code should get a blank frame and a preflight warning rather than a
 * broken editor.
 */
export function grid(layer: QrLayer): QrGrid | null {
  const data = payload(layer);

  if (data === '') return null;

  const key = `${layer.ecc}|${data}`;
  const hit = cache.get(key);

  if (hit !== undefined) return hit;

  let built: QrGrid | null = null;

  try {
    // Version 0 means "the smallest that fits", which is what keeps the modules
    // as large - and so as scannable - as the content allows.
    const code = qrcode(0, layer.ecc);

    code.addData(data);
    code.make();

    const size = code.getModuleCount();
    const count = size + QUIET * 2;
    const dark: boolean[] = new Array(count * count).fill(false);

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (code.isDark(row, col)) dark[(row + QUIET) * count + (col + QUIET)] = true;
      }
    }

    built = { count, dark };
  } catch {
    // The encoder throws when the data will not fit even the largest version.
    // That is a customer pasting an essay into a QR code, not a fault: null is
    // the answer, the panel says so in words, and preflight blocks the order.
    // Logging it as an error would train everyone to ignore the console.
    built = null;
  }

  if (cache.size >= LIMIT) {
    const oldest = cache.keys().next().value;

    if (oldest !== undefined) cache.delete(oldest);
  }

  cache.set(key, built);

  return built;
}

/**
 * How large one printed module would be, in millimetres.
 *
 * The number that decides whether the thing scans. A code squeezed onto a
 * business card with a long URL in it ends up with modules a press cannot hold.
 */
export function moduleMm(layer: QrLayer): number {
  const built = grid(layer);

  if (!built || built.count === 0) return 0;

  return Math.min(layer.w, layer.h) / built.count;
}
